/**
 * Utility functions for handling number input fields that allow blank values
 * This makes number inputs more user-friendly by allowing users to clear the field
 * and start typing from scratch without having to select all text first.
 */

/**
 * Converts a number input value to a display value
 * Returns empty string for 0 or falsy values, otherwise returns the number
 * @param {number} value - The numeric value
 * @returns {string|number} - Empty string for 0, otherwise the original value
 */
export const getNumberInputDisplayValue = (value) => {
    // Handle null, undefined, empty string, or empty array
    if (value === null || value === undefined || value === '') {
        return '';
    }
    
    // If it's exactly 0 (number), show empty string
    if (value === 0) {
        return '';
    }
    
    // For all other values (including decimals like 0.07, strings during typing), show the value
    return value;
};

/**
 * Converts a number input string value to a numeric value
 * Returns 0 for empty strings or invalid numbers
 * @param {string} value - The input string value
 * @returns {number} - The parsed number or 0
 */
export const parseNumberInputValue = (value) => {
    return value === '' ? 0 : parseFloat(value) || 0;
};

/**
 * Handles onChange for number inputs, storing the raw string value
 * @param {string} value - The input string value
 * @returns {string} - The raw string value (empty string for invalid numbers)
 */
export const handleNumberInputChange = (value) => {
    // If it's an empty string, return empty string
    if (value === '') {
        return '';
    }
    
    // Allow valid decimal number patterns including partial input
    // Matches: "1", "1.", "1.2", ".5", "0.07", "-1", "-1.2", etc.
    const validPattern = /^-?(\d*\.?\d*|\.?\d+)$/;
    
    // Also allow just a decimal point at the start
    if (value === '.' || value === '-.' || validPattern.test(value)) {
        return value;
    }
    
    // If it's not a valid pattern, return empty string
    return '';
};

/**
 * Creates a number input handler that updates state with raw string values
 * @param {Function} setState - Function to update state
 * @param {string} stateKey - The state key to update
 * @returns {Function} - The onChange handler function
 */
export const createNumberInputHandler = (setState, stateKey) => {
    return (value) => {
        const rawValue = handleNumberInputChange(value);
        setState(prevState => ({
            ...prevState,
            [stateKey]: rawValue
        }));
    };
};
